import type { BillingInvoice } from '@types/index';

export function formatUtcDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function utcMonthKey(value?: string | null, fallback = new Date()): string {
  const d = value ? new Date(value) : fallback;
  const source = Number.isNaN(d.getTime()) ? fallback : d;
  return `${source.getUTCFullYear()}-${String(source.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function utcMonthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  const d = new Date(Date.UTC(year || 1970, (month || 1) - 1, 1));
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function formatInvoiceAmount(amount?: number | null, currency = 'USD'): string {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return `${Number(amount).toFixed(2)} ${currency || 'USD'}`;
}

export function currentUtcMonthKey(): string {
  return utcMonthKey(new Date().toISOString());
}

export function groupInvoicesByUtcMonth(invoices: BillingInvoice[]): Array<{
  key: string;
  label: string;
  invoices: BillingInvoice[];
}> {
  const groups = new Map<string, BillingInvoice[]>();
  invoices.forEach((invoice) => {
    const key = utcMonthKey(invoice.date || invoice.startedAt);
    const list = groups.get(key) || [];
    list.push(invoice);
    groups.set(key, list);
  });
  if (!groups.size) {
    const key = currentUtcMonthKey();
    return [{ key, label: utcMonthLabel(key), invoices: [] }];
  }
  return Array.from(groups.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([key, list]) => ({ key, label: utcMonthLabel(key), invoices: list }));
}

export function invoiceHtml(invoice: BillingInvoice): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${invoice.invoiceNumber}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111739; padding: 32px; }
    h1 { font-size: 22px; margin: 0 0 6px; }
    p, td { font-size: 14px; color: #465069; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    td { padding: 8px 0; border-bottom: 1px solid #e2e8f0; }
    .muted { color: #64748b; font-size: 12px; margin-top: 24px; }
  </style>
</head>
<body>
  <h1>EncycGames invoice</h1>
  <p>${invoice.invoiceNumber}</p>
  <table>
    <tr><td>Date (UTC)</td><td>${formatUtcDate(invoice.date)}</td></tr>
    <tr><td>Description</td><td>${invoice.description || invoice.planLabel || '—'}</td></tr>
    <tr><td>Amount</td><td>${formatInvoiceAmount(invoice.amountUsd, invoice.currency)}</td></tr>
    <tr><td>Status</td><td>${invoice.invoiceStatus || invoice.status || 'paid'}</td></tr>
    <tr><td>Period</td><td>${formatUtcDate(invoice.startedAt)} – ${formatUtcDate(invoice.expiresAt)}</td></tr>
  </table>
  <p class="muted">EncycGames · amounts in ${invoice.currency || 'USD'} · dates in UTC.</p>
</body>
</html>`;
}

export function monthInvoicesHtml(label: string, invoices: BillingInvoice[]): string {
  const rows = invoices.length
    ? invoices
        .map(
          (invoice) =>
            `<tr>
              <td>${formatUtcDate(invoice.date)}</td>
              <td>${invoice.description || invoice.planLabel || ''}</td>
              <td>${invoice.invoiceStatus || invoice.status || 'paid'}</td>
              <td>${formatInvoiceAmount(invoice.amountUsd, invoice.currency)}</td>
              <td>${invoice.invoiceNumber}</td>
            </tr>`,
        )
        .join('')
    : '<tr><td colspan="5">No invoices in this month.</td></tr>';
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>EncycGames invoices · ${label}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111739; padding: 32px; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    .utc { color: #64748b; font-size: 12px; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 8px 6px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
    th { color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Invoices · ${label}</h1>
  <p class="utc">UTC</p>
  <table>
    <thead>
      <tr><th>Date (UTC)</th><th>Description</th><th>Status</th><th>Amount</th><th>Invoice</th></tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;
}

export function downloadHtml(filename: string, html: string) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
